// Hook personalizado para gestionar pedidos de compra

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  getAllPurchaseOrders,
  getPurchaseOrder,
  createPurchaseOrder,
  updatePurchaseOrderStatus,
  registerPayment,
  registerReceipt,
  completePurchaseOrder,
  updatePurchaseOrder
} from '../services/purchaseOrders.service'

/**
 * Hook para obtener todos los pedidos de compra
 */
export const usePurchaseOrders = (options = {}) => {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const optionsKey = JSON.stringify(options || {})
  const optionsRef = useRef({ key: optionsKey, value: options || {} })
  if (optionsRef.current.key !== optionsKey) {
    optionsRef.current = { key: optionsKey, value: options || {} }
  }
  const normalizedOptions = optionsRef.current.value

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getAllPurchaseOrders(normalizedOptions)
      setOrders(data)
    } catch (err) {
      console.error('Error obteniendo pedidos de compra:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [normalizedOptions])

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  return { orders, loading, error, refetch: fetchOrders }
}

/**
 * Hook para obtener un pedido de compra específico
 */
export const usePurchaseOrder = (orderId) => {
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!orderId) {
      setOrder(null)
      setLoading(false)
      return
    }

    const fetchOrder = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await getPurchaseOrder(orderId)
        setOrder(data)
      } catch (err) {
        console.error('Error obteniendo pedido de compra:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchOrder()
  }, [orderId])

  const refetch = useCallback(async () => {
    if (!orderId) return
    try {
      setLoading(true)
      setError(null)
      const data = await getPurchaseOrder(orderId)
      setOrder(data)
    } catch (err) {
      console.error('Error obteniendo pedido de compra:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [orderId])

  return { order, loading, error, refetch }
}

/**
 * Hook para gestionar operaciones de pedidos de compra
 */
export const usePurchaseOrderOperations = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const create = useCallback(async (orderData) => {
    try {
      setLoading(true)
      setError(null)
      const orderId = await createPurchaseOrder(orderData)
      return orderId
    } catch (err) {
      console.error('Error creando pedido de compra:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const updateStatus = useCallback(async (orderId, status, userId, userName) => {
    try {
      setLoading(true)
      setError(null)
      await updatePurchaseOrderStatus(orderId, status, userId, userName)
    } catch (err) {
      console.error('Error actualizando estado:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const addPayment = useCallback(async (orderId, supplierId, paymentData) => {
    try {
      setLoading(true)
      setError(null)
      await registerPayment(orderId, supplierId, paymentData)
    } catch (err) {
      console.error('Error registrando pago:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const addReceipt = useCallback(async (orderId, supplierId, receivedItems, updateStock, userId, userName) => {
    try {
      setLoading(true)
      setError(null)
      await registerReceipt(orderId, supplierId, receivedItems, updateStock, userId, userName)
    } catch (err) {
      console.error('Error registrando recepción:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const complete = useCallback(async (orderId, ratings, userId, userName) => {
    try {
      setLoading(true)
      setError(null)
      await completePurchaseOrder(orderId, ratings, userId, userName)
    } catch (err) {
      console.error('Error finalizando pedido:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const update = useCallback(async (orderId, updates) => {
    try {
      setLoading(true)
      setError(null)
      await updatePurchaseOrder(orderId, updates)
    } catch (err) {
      console.error('Error actualizando pedido:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    create,
    updateStatus,
    addPayment,
    addReceipt,
    complete,
    update,
    loading,
    error
  }
}

